import { formatCount } from '@/lib/formatters';

interface CampaignFunnelProps {
  clicks: number;
  installs: number;
  signups: number;
  appOpens?: number;
}

type Stage = {
  key: 'clicks' | 'installs' | 'signups' | 'appOpens';
  label: string;
  count: number;
};

function pct(num: number, denom: number): string {
  if (denom <= 0) return '—';
  return `${((num / denom) * 100).toFixed(1)}%`;
}

/**
 * Four-stage attribution funnel per spec §5.8.2:
 * Clicks → Installs → Signups → First Activity (app_open).
 *
 * Each row shows the absolute count, the share of the top (clicks) and
 * the drop-off from the previous stage — the most actionable number for
 * a marketer trying to find where the funnel leaks.
 */
export function CampaignFunnel({ clicks, installs, signups, appOpens }: CampaignFunnelProps) {
  const stages: Stage[] = [
    { key: 'clicks', label: 'Clicks', count: clicks },
    { key: 'installs', label: 'Installs', count: installs },
    { key: 'signups', label: 'Signups', count: signups },
    { key: 'appOpens', label: 'First activity', count: appOpens ?? 0 },
  ];
  const top = stages[0].count;
  const max = Math.max(1, top);

  return (
    <ul className="space-y-2">
      {stages.map((s, i) => {
        const widthPct = (s.count / max) * 100;
        const shareOfTop = pct(s.count, top);
        const dropFromPrev = i === 0 ? null : pct(s.count, stages[i - 1].count);
        return (
          <li key={s.key} className="relative">
            <div
              className="absolute inset-y-0 left-0 rounded-md bg-primary/10"
              style={{ width: `${widthPct}%` }}
              aria-hidden
            />
            <div className="relative flex items-center justify-between gap-3 px-3 py-2 text-sm">
              <div className="flex min-w-0 items-center gap-3">
                <span className="font-medium">{s.label}</span>
                {dropFromPrev && (
                  <span className="text-xs text-muted-foreground">
                    {dropFromPrev} of previous
                  </span>
                )}
              </div>
              <div className="flex shrink-0 items-baseline gap-3 tabular-nums">
                <span className="text-xs text-muted-foreground">
                  {shareOfTop} of clicks
                </span>
                <span className="font-medium">{formatCount(s.count)}</span>
              </div>
            </div>
          </li>
        );
      })}
    </ul>
  );
}
